import { ForwardedRef, forwardRef, ReactNode } from 'react';
import { LookupValue, PropsWithForwardedRef } from '../../../types/types';
import { RadioGroup, RadioGroupProps } from '../RadioGroup';
import { Select, SelectProps } from '../Select';

interface CommonProps<TValue = string> {
  id?: string;
  name: string;
  label: string;
  helpMessage?: ReactNode;
  value?: TValue;
  onChange: (next: TValue) => void;
  options: LookupValue<TValue>[];

  /** Optional function that can be used to get the unique key from the value type */
  getOptionKey?: (value: TValue) => string;
  errorMessage?: string;
}

export type RadioSelectSelectProps<TValue = string> = Pick<
  SelectProps<TValue>,
  'disabled' | 'required' | 'optional' | 'placeholder' | 'className'
>;

export type RadioSelectRadioProps<TValue = string> = Pick<RadioGroupProps<TValue>, 'vertical' | 'margin'>;

export type RadioSelectProps<TValue = string> = CommonProps<TValue> & {
  /**
   * The maximum number of radio buttons that are allowed to be shown before the control will show as a
   * Select box instead. Defaults to 6.
   */
  maxRadioButtons?: number;

  /**
   * Additional properties to pass when a radio button control is shown.
   */
  radioProps?: RadioSelectRadioProps<TValue>;

  /**
   * Additional properties to pass when a select is shown.
   */
  selectProps?: RadioSelectSelectProps<TValue>;
};

const RadioSelectWithRef = <TValue extends unknown = string>(
  { maxRadioButtons, options, radioProps, selectProps, ...commonProps }: RadioSelectProps<TValue>,
  ref: ForwardedRef<HTMLInputElement | HTMLSelectElement>
) => {
  const max = maxRadioButtons != null ? maxRadioButtons : 6;
  const useRadioGroup = max > 0 && options.length <= max!;

  return useRadioGroup ? (
    <RadioGroup<TValue>
      options={options}
      {...commonProps}
      {...(radioProps ?? {})}
      ref={ref as ForwardedRef<HTMLInputElement>}
    />
  ) : (
    <Select<TValue>
      options={options}
      {...commonProps}
      {...(selectProps ?? {})}
      ref={ref as ForwardedRef<HTMLSelectElement>}
    />
  );
};

export const RadioSelect = forwardRef(RadioSelectWithRef) as <TValue extends unknown = string>(
  props: PropsWithForwardedRef<RadioSelectProps<TValue>, HTMLInputElement | HTMLSelectElement>
) => ReturnType<typeof RadioSelectWithRef>;
